'use client';

import * as React from 'react';
import { trackSegmentEvent } from 'app/utils';

import { cn } from 'lib/utils';

import { useAuth } from 'providers/AuthProvider';
import { useAppSelector } from 'app/store';
import { selectUserInfo } from 'app/store/user';
import { useState } from 'react';
import { ThemeMode } from 'providers/ThemeProvider';
import useTheme from 'hooks/useTheme';
import { ACTION, SOURCE } from 'app/analytics';
import { getSwitchLink, getStripePortalUrl } from 'config/constants';
import { env } from 'config/env';
import { useFlags } from 'providers/FlagsProvider';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { DevelopersDialog, OriginalAccount } from './components/DevelopersDialog';
import { localStorageAdminAccount } from 'app/localstorage';
import useNotifications from 'hooks/useNotifications';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { canWrite } from 'app/access';

interface AccountDropdownProps {
  className?: string;
}

export default function AccountDropdown({ className }: AccountDropdownProps) {
  const { theme, switchTheme } = useTheme();

  const userInfo = useAppSelector(selectUserInfo);
  const { displayName, email, photoURL, apiKey, billingPeriodSettings } = userInfo || {};
  const canManageBilling = canWrite(userInfo, 'billing');
  const { notificationsToDisplay, disableNotification } = useNotifications();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (notificationsToDisplay.length) {
      notificationsToDisplay.forEach((notification) => {
        toast(notification.title, {
          id: notification.id,
          action: {
            label: 'Go to account',
            onClick: () => {
              trackSegmentEvent(SOURCE.CONSOLE_MENU, ACTION.CLICK, 'account_notification_link');
              navigate(notification.link);
            },
          },
        });
        disableNotification(notification.id);
      });
    }
  }, []);

  const { ryeAdminAccount: isAdminUser } = useFlags();
  const originalAccount = localStorageAdminAccount.get<OriginalAccount>();

  const { logout, assumeDeveloperIdentity } = useAuth();

  const [open, setOpen] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);

  const handleSwitchThemeClick = () => {
    switchTheme();
    trackSegmentEvent(SOURCE.CONSOLE_MENU, ACTION.CLICK, 'theme_button');
    setOpen(false);
  };

  const handleLogoutClick = () => {
    logout();
    trackSegmentEvent(SOURCE.CONSOLE_MENU, ACTION.CLICK, 'logout_button');
  };

  const switchLabel = () => {
    switch (env.VITE_NODE_ENV) {
      case 'staging':
      case 'development':
        return 'Production';
      default:
        return 'Staging';
    }
  };

  const handleEnvSwitch = () => {
    window.location.href = getSwitchLink();
  };

  const handleDialogOpen = () => {
    setOpenDialog(true);
    setOpen(false);
  };

  const handleBackToAdmin = async () => {
    if (!originalAccount?.id || !originalAccount?.token) {
      return;
    }

    await assumeDeveloperIdentity(originalAccount.id, apiKey, originalAccount.token, false);
    localStorageAdminAccount.remove();
    setOpen(false);
    window.location.reload();
  };

  const handleViewSubscription = () => {
    window.open(getStripePortalUrl(), '_blank');
  };

  const initial = displayName?.charAt(0).toUpperCase() || email?.charAt(0).toUpperCase() || 'U';
  const secondInitial = displayName?.split(/\s+/)[1]?.charAt(0).toUpperCase();
  const initials = secondInitial ? `${initial}${secondInitial}` : initial;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DevelopersDialog
        open={openDialog}
        onOpenChange={setOpenDialog}
        onLogin={() => window.location.reload()}
      />
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Account menu for ${displayName || email || 'user'}`}
          aria-haspopup="menu"
          aria-expanded={open}
          className={cn(
            'relative inline-grid h-8 w-8 place-items-center overflow-hidden bg-[var(--accent)] font-mono text-xs font-semibold text-[var(--accent-fg)] transition-shadow [transition-duration:120ms] hover:[box-shadow:0_0_0_2px_var(--bg),0_0_0_4px_var(--accent-2)] focus-visible:outline-none focus-visible:[box-shadow:0_0_0_2px_var(--bg),0_0_0_4px_var(--text)] active:translate-y-px',
            open && '[box-shadow:0_0_0_2px_var(--bg),0_0_0_4px_var(--text)]',
            className,
          )}
        >
          {/* Initials sit underneath; the photo, when it loads, covers
              them. If the photo URL fails the initials remain visible. */}
          <span aria-hidden="true">{initials}</span>
          {photoURL && (
            <img
              src={photoURL}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[260px]">
        {(displayName || email) && (
          <>
            <DropdownMenuLabel className="overflow-hidden text-ellipsis whitespace-nowrap font-mono text-[11px] font-normal text-[var(--text-muted)]">
              {email || displayName}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuGroup>
          {isAdminUser && !originalAccount ? (
            <DropdownMenuItem onClick={handleDialogOpen}>
              Assume Developer's identity
            </DropdownMenuItem>
          ) : (
            originalAccount?.id &&
            originalAccount.token && (
              <DropdownMenuItem onClick={handleBackToAdmin}>
                Go back to original account
              </DropdownMenuItem>
            )
          )}
          {billingPeriodSettings?.__typename === 'BillingSubscription' && canManageBilling && (
            <DropdownMenuItem onClick={handleViewSubscription}>
              Manage Subscription
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={handleEnvSwitch}>
            {`Switch to ${switchLabel()}`}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleSwitchThemeClick}>
            {`Switch to ${theme === ThemeMode.Light ? ThemeMode.Dark : ThemeMode.Light} mode`}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem variant="destructive" onClick={handleLogoutClick}>
            Logout
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
